import { useEffect, useMemo, useRef, useState } from 'react';
import { GLOSSARY } from '../data/glossary';
import { useChime } from '../hooks/useChime';
import styles from './MemoryPairs.module.css';

interface MemoryCard {
  cardId: string;
  termIndex: number;
  side: 'term' | 'definition';
  matched: boolean;
  flipped: boolean;
}

interface MemoryPairsProps {
  pairs?: number;
  onDone: (stats: { time: number; mismatches: number }) => void;
  onExit: () => void;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

export function MemoryPairs({ pairs = 6, onDone, onExit }: MemoryPairsProps) {
  const chime = useChime();

  const initialDeck = useMemo<MemoryCard[]>(() => {
    const picked = shuffle(GLOSSARY.map((_, i) => i)).slice(0, pairs);
    const cards: MemoryCard[] = [];
    picked.forEach((termIndex) => {
      cards.push({
        cardId: `t-${termIndex}`,
        termIndex,
        side: 'term',
        matched: false,
        flipped: false,
      });
      cards.push({
        cardId: `d-${termIndex}`,
        termIndex,
        side: 'definition',
        matched: false,
        flipped: false,
      });
    });
    return shuffle(cards);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [deck, setDeck] = useState<MemoryCard[]>(initialDeck);
  const [flippedIds, setFlippedIds] = useState<string[]>([]);
  const [mismatches, setMismatches] = useState(0);
  const [startTime] = useState(() => Date.now());
  const [elapsed, setElapsed] = useState(0);
  const lockRef = useRef(false);

  // Live elapsed timer
  useEffect(() => {
    const id = window.setInterval(() => setElapsed(Math.floor((Date.now() - startTime) / 1000)), 250);
    return () => window.clearInterval(id);
  }, [startTime]);

  function flip(card: MemoryCard) {
    if (lockRef.current) return;
    if (card.flipped || card.matched) return;
    if (flippedIds.length === 2) return;

    const next = deck.map((c) =>
      c.cardId === card.cardId ? { ...c, flipped: true } : c
    );
    const newFlipped = [...flippedIds, card.cardId];
    setDeck(next);
    setFlippedIds(newFlipped);

    if (newFlipped.length === 2) {
      const [aId, bId] = newFlipped;
      const a = next.find((c) => c.cardId === aId);
      const b = next.find((c) => c.cardId === bId);
      if (a && b && a.termIndex === b.termIndex && a.side !== b.side) {
        // Match!
        chime.play('match');
        lockRef.current = true;
        window.setTimeout(() => {
          setDeck((d) =>
            d.map((c) =>
              c.cardId === a.cardId || c.cardId === b.cardId
                ? { ...c, matched: true }
                : c
            )
          );
          setFlippedIds([]);
          lockRef.current = false;
        }, 600);
      } else {
        // Mismatch
        setMismatches((m) => m + 1);
        lockRef.current = true;
        window.setTimeout(() => {
          setDeck((d) =>
            d.map((c) =>
              c.cardId === aId || c.cardId === bId ? { ...c, flipped: false } : c
            )
          );
          setFlippedIds([]);
          lockRef.current = false;
        }, 1100);
      }
    }
  }

  // Win condition
  useEffect(() => {
    if (deck.length > 0 && deck.every((c) => c.matched)) {
      const totalTime = Math.floor((Date.now() - startTime) / 1000);
      chime.play('win');
      window.setTimeout(() => onDone({ time: totalTime, mismatches }), 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deck]);

  return (
    <section className={styles.frame}>
      <header className={styles.header}>
        <span className={styles.label}>memory pairs</span>
        <div className={styles.metrics}>
          <span className={styles.metric}>{elapsed}s</span>
          <span className={styles.metric}>misses: {mismatches}</span>
          <button type="button" className={styles.exit} onClick={onExit}>
            exit
          </button>
        </div>
      </header>

      <div className={styles.grid}>
        {deck.map((card) => {
          const term = GLOSSARY[card.termIndex]!;
          const showFace = card.flipped || card.matched;
          return (
            <button
              key={card.cardId}
              type="button"
              className={`${styles.card} ${showFace ? styles.faceUp : ''} ${card.matched ? styles.matched : ''}`}
              onClick={() => flip(card)}
              aria-label={showFace ? (card.side === 'term' ? term.term : term.definition) : 'face-down card'}
            >
              <span className={styles.cardInner}>
                <span className={styles.cardBack} aria-hidden={showFace}>
                  ✦
                </span>
                <span className={styles.cardFront} aria-hidden={!showFace}>
                  {card.side === 'term' ? (
                    <span className={styles.term}>{term.term}</span>
                  ) : (
                    <span className={styles.definition}>{term.definition}</span>
                  )}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
